import { mentionDocExtension as BaseMentionDocExtension } from '@copera/tiptap/extensions';

import { NodeViewProps } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { memo, useMemo } from 'react';
import { MentionDocNodeView } from '../private/components/MentionDocNodeView';
import { getExtensionOption } from '../private/utils/getExtensionOption';

const DocLink = memo(({ node, editor }: NodeViewProps) => {
  const id = node.attrs.id;

  const doc = useMemo(() => {
    if (!id) return null;
    const options = getExtensionOption(editor, 'mentionDoc');
    return options.getDocumentById(id);
  }, [editor, id]);

  if (!doc) return null;

  return (
    <NodeViewWrapper key={id} as="span">
      <MentionDocNodeView doc={doc} />
    </NodeViewWrapper>
  );
});

export const mentionDocExtended = BaseMentionDocExtension.extend({
  addOptions() {
    return {
      registerSubDocument: false,
    };
  },
  addCommandOption(this) {
    return [
      ...(this.options?.registerSubDocument
        ? [
            {
              title: 'Create sub document',
              description: 'Create a new sub document',
              searchTerms: ['sub', 'document'],
              icon: 'tabler:file-plus',
              // @ts-expect-error
              command: async ({ editor, range }) => {
                const options = getExtensionOption(editor, 'mentionDoc');
                if (!options)
                  throw new Error('mentionDoc extension options not found');
                const newDoc = await options.onCreateNewDocument();
                editor.commands.mentionDoc({ range, id: newDoc.id });
              },
            },
          ]
        : []),
      {
        title: 'Link a document',
        description: 'Link an existing document',
        searchTerms: ['link', 'document'],
        icon: 'tabler:file-dots',
        nextMenu: {
          type: 'mentionDoc',
        },
      },
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(DocLink, {
      contentDOMElementTag: 'span',
    });
  },
});

export const mentionDocExtension = mentionDocExtended.configure({
  registerSubDocument: true,
});
